import { Alert, AlertDescription, Button, showConfirmationPrompt } from '@bpinternal/ui-kit-next'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { IntegrationDetails } from '~/features/integrations/components'
import {
  useMakeIntegrationPublic,
  useRequestIntegrationVerification,
} from '~/hooks/integrations/useIntegrationMutations'
import { integrationQueryOptions } from '~/queries'
import { cn, isBotpressWorkspace } from '~/utils'

export const Route = createFileRoute('/workspaces/$workspaceId/integrations/$integrationId/')({
  component: Component,
})

function Component() {
  const { integrationId, workspaceId } = Route.useParams()
  const integration = useSuspenseQuery(integrationQueryOptions({ integrationId, workspaceId })).data
  const { mutate: publishIntegration, isPending: publishingIntegration } = useMakeIntegrationPublic({
    workspaceId,
    integrationId,
  })
  const { mutate: requestIntegrationVerification } = useRequestIntegrationVerification({ workspaceId, integrationId })

  const bpWorkspace = isBotpressWorkspace(workspaceId)
  let calloutProps:
    | {
        variant: 'warning' | 'info' | 'destructive'
        description: React.ReactNode
        action?: React.ReactNode
      }
    | undefined = undefined

  if (!integration.public && bpWorkspace) {
    calloutProps = {
      variant: 'warning',
      description: (
        <p className="text-sm">
          Releasing an integration in the Botpress official workspace must be done using the Cli or the CI. It is not
          possible to release integrations in the Botpress Hub using the Botpress Dashboard.
        </p>
      ),
      action: (
        <Button disabled={publishingIntegration} onClick={() => requestIntegrationVerification()}>
          {publishingIntegration ? 'Requesting verification...' : 'Request verification'}
        </Button>
      ),
    }
  } else if (!integration.public) {
    calloutProps = {
      variant: 'info',
      description: (
        <p className="text-sm">
          This is a <strong>private</strong> integration.
        </p>
      ),
      action: (
        <Button
          className="self-center"
          variant="ghost"
          disabled={publishingIntegration}
          onClick={() => {
            void showConfirmationPrompt(
              <p className="text-sm">
                By publishing content to the Botpress Hub, you acknowledge having read and accepted the Botpress Hub
                Terms. In particular, you warrant that your content is free from any malicious code and is not
                infringing on the rights of others. You will be solely responsible for your content and Botpress or
                Botpress users may seek compensation from you if your content causes damages.&nbsp;
                <a
                  href="https://botpress.com/legal/hub-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-700 underline hover:text-primary-900"
                >
                  Read full terms
                </a>
              </p>,
              {
                confirmLabel: 'Publish',
                cancelLabel: 'Cancel',
                title: `Publish ${integration.title} to Botpress Hub`,
              }
            ).then(() => publishIntegration())
          }}
        >
          {publishingIntegration ? 'Publishing...' : 'Make Public'}
        </Button>
      ),
    }
  } else if (integration.verificationStatus === 'unapproved') {
    calloutProps = {
      variant: 'warning',
      description: <p className="text-sm">{"This public integration isn't verified."}</p>,
      action: (
        <Button
          className="self-center text-amber-700 hover:bg-amber-200 hover:text-amber-700"
          variant="ghost"
          disabled={publishingIntegration}
          onClick={() => requestIntegrationVerification()}
        >
          {publishingIntegration ? 'Requesting verification...' : 'Request verification'}
        </Button>
      ),
    }
  } else if (integration.verificationStatus === 'pending') {
    calloutProps = {
      variant: 'info',
      description: (
        <p className="text-sm">
          This public integration verification is <strong>in progress</strong>.
        </p>
      ),
    }
  } else if (integration.verificationStatus === 'rejected') {
    calloutProps = {
      variant: 'destructive',
      description: (
        <p className="text-sm">
          This public integration verification was rejected. Your integration is most likely not working as expected.
          Make sure to fix the issues, publish your changes and resubmit it for verification. For any questions you can{' '}
          <a href="mailto:hub-applications@botpress.com" className="text-primary-700 underline hover:text-primary-900">
            Contact us
          </a>
        </p>
      ),
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {calloutProps && (
        <Alert
          className={cn('flex items-center justify-between', {
            'border-amber-200 bg-amber-100 text-amber-700': calloutProps.variant === 'warning',
            'border-primary-200 bg-primary-100 text-primary-900': calloutProps.variant === 'info',
            'border-red-200 bg-red-200 text-red-700': calloutProps.variant === 'destructive',
          })}
        >
          <div className="flex flex-col">
            <AlertDescription>{calloutProps.description}</AlertDescription>
          </div>
          <div className="self-end">{calloutProps.action}</div>
        </Alert>
      )}
      <IntegrationDetails integration={integration} />
    </div>
  )
}
